import React, { useMemo } from 'react';

import { Contract } from '@work4all/models/lib/Classes/Contract.entity';
import { SortDirection } from '@work4all/models/lib/DataProvider';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { EntityTableV8, IEntityTableV8 } from './entity-table/EntityTableV8';
import { useEntityDataTableV8 } from './entity-table/use-entity-data-table-v8';
import { ErpRessourceClassCell } from './ErpRessourceClassCell';
import { useErpTable } from './offers-table/erp-common/use-erp-table';
import { useCloneConvertErpActions } from './offers-table/hooks/use-clone-convert-erp';
import { useErpPlanningColumns } from './planning-columns/use-erp-planning-columns';
import schema from './schemata/contract-table-schema.json';
import { useErpHandlersTemplateV8 } from './use-erp-handlers-template-v8';

const defaultSort = [
  { field: 'contractDate', direction: SortDirection.DESCENDING },
];

const secondarySort = [
  { field: 'contractNumber', direction: SortDirection.DESCENDING },
];

export function ContractsTable(props: IEntityTableV8) {
  const erpPlanningColumns = useErpPlanningColumns({
    entity: Entities.contract,
  });

  const transformedSchema = useMemo(() => {
    return {
      ...schema,
      columns: [...schema.columns, ...erpPlanningColumns],
    } as never;
  }, [erpPlanningColumns]);

  const { ...dataTable } = useEntityDataTableV8<Contract, never>({
    ...props,
    schema: transformedSchema,
    cells: {
      ErpRessourceClass: ErpRessourceClassCell,
    },
    defaultSort,
    secondarySort,
    enableFooter: true,
    forceRequestFields: {
      positionList: [
        {
          id: null,
          unit: null,
          amount: null,
          article: {
            id: null,
            name: null,
            number: null,
            ressourceClassId: null,
          },
        },
      ],
    } as Contract,
    rowModifiers: (value) => {
      return {
        isFaded: value.isClosed && value.frozen,
        isShade1: !value.isClosed && value.frozen,
      };
    },
  });

  const template = useErpHandlersTemplateV8(dataTable.table);

  const { actions } = useCloneConvertErpActions({
    entity: Entities.contract,
    data: dataTable.selectedEntities,
  });

  useErpTable(Entities.contract, dataTable.data.length === 0);

  const custom = useMemo(
    () => ({
      left: actions,
    }),
    [actions]
  );

  return (
    <EntityTableV8
      {...props}
      {...dataTable}
      template={template}
      actions={{ custom }}
      displayFooter
    />
  );
}
